import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { PageHeader, Tabs, TrackerTable } from "@/components/shared";
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, AlertOctagon, CheckCircle2, Info,
  Building2, GitBranch, UsersRound, Eye, FlaskConical, ClipboardList,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "business", label: "Business Growth" },
  { id: "branch", label: "Branch Growth" },
  { id: "team", label: "Team Performance" },
  { id: "competitors", label: "Competitors" },
  { id: "rnd", label: "Competitor R&D" },
  { id: "actions", label: "Actions" },
];

const INSIGHT_ICON = { positive: CheckCircle2, warning: AlertTriangle, danger: AlertOctagon, neutral: Info };
const INSIGHT_COLOR = { positive: "text-[#059669]", warning: "text-[#F59E0B]", danger: "text-[#DC2626]", neutral: "text-slatetext" };

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const num = (n) => Number(n || 0).toLocaleString("en-IN");

function GrowthPill({ value }) {
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const color = value > 0 ? "text-[#059669]" : value < 0 ? "text-[#DC2626]" : "text-slatetext";
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}><Icon className="w-3.5 h-3.5" />{value > 0 ? "+" : ""}{value}%</span>;
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-surface-hover bg-surface p-5" data-testid={`mm-kpi-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="text-xs text-slatetext mb-1">{label}</div>
      <div className="font-heading text-2xl font-extrabold">{value}</div>
      {sub !== undefined && <div className="mt-1">{sub}</div>}
    </div>
  );
}

export default function ManagerDashboard() {
  const [active, setActive] = useState("overview");
  const { data, isLoading } = useQuery({ queryKey: ["manager-dashboard"], queryFn: async () => (await api.get("/manager-dashboard")).data });

  return (
    <div>
      <PageHeader title="Marketing Manager" subtitle="Marketing → Business Growth → Branch Growth → Team → Competitor → R&D → Actions" />
      <Tabs tabs={TABS} active={active} onChange={setActive} />

      {active === "overview" && (
        isLoading || !data ? <div className="text-slatetext py-10 text-center">Loading…</div> : (
          <div className="space-y-6">
            <div className="text-sm text-slatetext">Period: <span className="text-gold font-medium">{data.month}</span> vs previous <span className="text-white">{data.prev_month}</span> · Field Marketing is tracked in its separate tool.</div>

            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}>
              <KpiCard label="Marketing Target" value={inr(data.overview.target)} />
              <KpiCard label="Marketing Actual" value={inr(data.overview.actual)} sub={<GrowthPill value={Math.round((data.overview.achievement - 100) * 10) / 10} />} />
              <KpiCard label="Achievement %" value={`${data.overview.achievement}%`} />
              <KpiCard label="Marketing Revenue" value={inr(data.overview.revenue)} />
              <KpiCard label="Marketing Spend" value={inr(data.overview.spend)} />
              <KpiCard label="ROI %" value={`${data.overview.roi}%`} />
              <KpiCard label="Total Leads" value={num(data.overview.leads)} />
              <KpiCard label="Qualified Leads" value={num(data.overview.qualified_leads)} />
              <KpiCard label="Consultations" value={num(data.overview.consultations)} />
              <KpiCard label="Conversion %" value={`${data.overview.conversion}%`} />
              <KpiCard label="Audience / Reach" value={num(data.overview.reach)} />
            </div>

            {/* Attention Required */}
            <div className="rounded-lg border border-[#DC2626]/30 bg-surface p-5" data-testid="mm-attention">
              <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5 text-[#DC2626]" /><h3 className="font-heading font-bold">Attention Required</h3></div>
              <div className="grid md:grid-cols-2 gap-2">
                {data.attention.map((a, i) => {
                  const Icon = INSIGHT_ICON[a.type] || Info;
                  return <div key={i} className="flex items-start gap-2.5 text-sm py-1"><Icon className={`w-4 h-4 mt-0.5 shrink-0 ${INSIGHT_COLOR[a.type]}`} /><span className="text-slate-200">{a.text}</span></div>;
                })}
              </div>
            </div>

            {/* Competitor/R&D/Action counts */}
            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))" }}>
              <KpiCard label="Competitors Followed" value={num(data.counts.competitors)} />
              <KpiCard label="Active Competitors" value={num(data.counts.active_competitors)} />
              <KpiCard label="R&D Activities" value={num(data.counts.rnd_activities)} />
              <KpiCard label="New Insights (month)" value={num(data.counts.new_insights)} />
              <KpiCard label="Actions Created" value={num(data.counts.actions_created)} />
              <KpiCard label="Actions Completed" value={num(data.counts.actions_completed)} />
              <KpiCard label="Pending Actions" value={num(data.counts.actions_pending)} />
            </div>

            {/* Growth comparison */}
            <div className="rounded-lg border border-surface-hover bg-surface overflow-hidden">
              <div className="p-5 border-b border-surface-hover"><h3 className="font-heading font-bold">Marketing Growth — Previous vs Current Month</h3></div>
              <table className="w-full text-sm" data-testid="mm-comparison">
                <thead><tr className="text-slatetext text-xs uppercase border-b border-surface-hover">
                  <th className="px-4 py-3 text-left">Metric</th><th className="px-4 py-3 text-right">{data.prev_month}</th><th className="px-4 py-3 text-right">{data.month}</th><th className="px-4 py-3 text-right">Growth</th>
                </tr></thead>
                <tbody>
                  {data.comparison.map((c) => (
                    <tr key={c.metric} className="border-b border-surface-hover/60 hover:bg-surface-hover/40 transition-colors duration-200">
                      <td className="px-4 py-3">{c.metric}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slatetext">{num(c.previous)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">{num(c.current)}</td>
                      <td className="px-4 py-3 text-right"><GrowthPill value={c.growth} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {active === "business" && (
        <div className="space-y-6">
          <ComputedTable icon={Building2} title="Business-wise Marketing Growth" testid="mm-business-table"
            rows={data?.business} cols={[
              { k: "business", label: "Business" }, { k: "target", label: "Target", money: true }, { k: "actual", label: "Actual", money: true },
              { k: "achievement", label: "Achv %", pct: true }, { k: "leads", label: "Leads", num: true }, { k: "revenue", label: "Revenue", money: true }, { k: "growth", label: "Growth", growth: true },
            ]} />
          <TrackerTable id="business-performance" />
        </div>
      )}

      {active === "branch" && (
        <div className="space-y-6">
          <ComputedTable icon={GitBranch} title="Branch-wise Marketing Growth" testid="mm-branch-table"
            rows={data?.branch} cols={[
              { k: "branch", label: "Branch" }, { k: "leads", label: "Leads", num: true }, { k: "qualified_leads", label: "Qualified", num: true },
              { k: "consultations", label: "Consult.", num: true }, { k: "conversion", label: "Conv %", pct: true }, { k: "revenue", label: "Revenue", money: true },
              { k: "spend", label: "Spend", money: true }, { k: "roi", label: "ROI %", pct: true }, { k: "growth", label: "Growth", growth: true },
            ]} />
          <TrackerTable id="branch-performance" />
        </div>
      )}

      {active === "team" && (
        <ComputedTable icon={UsersRound} title="Marketing Team Performance (auto from employee data)" testid="mm-team-table"
          rows={data?.team} cols={[
            { k: "employee", label: "Employee" }, { k: "role", label: "Role" }, { k: "metric", label: "Main KPI" },
            { k: "target", label: "Target", num: true }, { k: "actual", label: "Actual", num: true }, { k: "achievement", label: "Achv %", pct: true }, { k: "status", label: "Status" },
          ]} />
      )}

      {active === "competitors" && <TrackerTable id="competitors" />}
      {active === "rnd" && <TrackerTable id="competitor-rnd" />}
      {active === "actions" && <TrackerTable id="manager-actions" />}
    </div>
  );
}

function ComputedTable({ icon: Icon, title, rows = [], cols, testid }) {
  return (
    <div className="rounded-lg border border-surface-hover bg-surface overflow-hidden" data-testid={testid}>
      <div className="p-5 border-b border-surface-hover flex items-center gap-2"><Icon className="w-5 h-5 text-gold" /><h3 className="font-heading font-bold">{title}</h3></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-slatetext text-xs uppercase border-b border-surface-hover">
            {cols.map((c) => <th key={c.k} className={`px-4 py-3 ${c.num || c.money || c.pct || c.growth ? "text-right" : "text-left"}`}>{c.label}</th>)}
          </tr></thead>
          <tbody>
            {(rows || []).map((r, i) => (
              <tr key={i} className="border-b border-surface-hover/60 hover:bg-surface-hover/40 transition-colors duration-200">
                {cols.map((c) => (
                  <td key={c.k} className={`px-4 py-3 ${c.num || c.money || c.pct || c.growth ? "text-right tabular-nums" : "text-left"}`}>
                    {c.growth ? <GrowthPill value={r[c.k]} /> : c.money ? inr(r[c.k]) : c.pct ? `${r[c.k]}%` : c.num ? num(r[c.k]) : (r[c.k] || "—")}
                  </td>
                ))}
              </tr>
            ))}
            {(!rows || rows.length === 0) && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-slatetext">No data yet. Add entries below.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
